import {Check, Clock, Image, Video} from '@nlabs/gothamjs/icons';

import {PreviewFrame} from '../PreviewFrame/PreviewFrame.js';

import type {Asset, GenerationJob, Shot} from '@reactorbox/shared';
import type {ReactElement} from 'react';

interface ShotCardProps {
  readonly assets: Asset[];
  readonly index: number;
  readonly jobs: GenerationJob[];
  readonly onSelect: () => void;
  readonly selected: boolean;
  readonly shot: Shot;
}

export const ShotCard = ({assets, index, jobs, onSelect, selected, shot}: ShotCardProps): ReactElement => {
  const asset = assets.find((item) => item.id === shot.approvedAssetId) || assets[0];
  const pending = jobs.some((job) => !['succeeded', 'failed'].includes(job.status));
  const status = pending ? 'Processing' : 'Draft';
  const readyStatus = assets.length ? 'Ready to review' : status;

  return (
    <button
      aria-pressed={selected}
      className={`group w-[184px] shrink-0 rounded-[20px] border p-1.5 text-left transition duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#c2a4ff] ${selected ? 'border-[#d1b6ff]/75 bg-[#bb98ee]/10' : 'border-white/10 bg-white/[0.025] hover:border-white/25 hover:bg-white/5'}`}
      onClick={onSelect}
      type="button"
    >
      <div className="relative h-[102px] overflow-hidden rounded-[14px] bg-[#242128]">
        {asset?.mediaUrl && asset.kind === 'image' ? (
          <img alt={shot.title} className="size-full object-cover" src={asset.mediaUrl} />
        ) : (
          <PreviewFrame artwork="project" className="size-full" index={index} label={`${shot.title} illustrative placeholder`} />
        )}
        <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-black/15" />
        <span className="absolute top-2 left-2 flex size-6 items-center justify-center rounded-full bg-black/30 text-[9px] font-medium text-white/90 backdrop-blur-md">
          {String(index + 1).padStart(2, '0')}
        </span>
        {shot.approvedAssetId && (
          <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-[#c9f2d3]/90 px-2 py-1 text-[8px] font-semibold text-[#243c2b]">
            <Check size={9} />Approved
          </span>
        )}
        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[9px] text-white/90">
          <Clock size={10} />{shot.duration}s
        </span>
        <span className="absolute right-2 bottom-2 text-[8px] text-white/65">{shot.resolution}</span>
      </div>
      <div className="px-2 pt-2.5 pb-2">
        <h3 className={`truncate text-[11px] font-medium ${selected ? 'text-white' : 'text-white/70'}`}>{shot.title}</h3>
        <div className="mt-2 flex items-center justify-between gap-2 text-[9px] text-white/35">
          <span>{shot.approvedAssetId ? 'Approved take' : readyStatus}</span>
          <span className="inline-flex items-center gap-1">
            {asset?.kind === 'video' ? <Video size={11} /> : <Image size={11} />}{assets.length} takes
          </span>
        </div>
      </div>
    </button>
  );
};
